import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { learnMasterclassModuleCompletionPath } from "@/routes"

/**
 * OKS-602 — Creator-side "Mark module as complete" form.
 *
 * **Crew hard rule (P0):** Inertia `<Form>` from `@inertiajs/react` with
 * a plain shadcn `Button`. NO shadcn `Form`, NO `react-hook-form`, NO
 * `zod`. The request body is intentionally EMPTY — the URL identifies
 * the module + masterclass; the server reads no body params.
 *
 * `processing` swaps the button to a spinner + "Marking…" (UX Spec §4).
 * Positioning (mobile sticky / inline) is owned by the parent page.
 */
export interface CreatorModuleMarkCompleteFormProps {
  masterclassId: number
  moduleId: number
  className?: string
}

export function CreatorModuleMarkCompleteForm({
  masterclassId,
  moduleId,
  className,
}: CreatorModuleMarkCompleteFormProps) {
  const { t } = useTranslation()

  return (
    <Form
      action={learnMasterclassModuleCompletionPath(masterclassId, moduleId)}
      method="post"
      className={className}
      data-testid="learn-module-mark-complete-form"
    >
      {({ processing }) => (
        <Button
          type="submit"
          size="lg"
          disabled={processing}
          className="w-full sm:w-auto"
          data-testid="learn-module-mark-complete-button"
        >
          {processing ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              {t("learn.module.marking")}
            </>
          ) : (
            t("learn.module.mark_complete")
          )}
        </Button>
      )}
    </Form>
  )
}
